import { memo, useMemo, useState } from "react";

import type { AssetListItem } from "@/lib/api";
import { formatBytes } from "@/lib/format";
import { podeRenderizarComCamadas, podeTrocarViewer } from "@/lib/viewerKindOverride";
import { useFileManagerStore } from "@/state/fileManagerStore";

import { useAuthStore } from "@/state/authStore";

import { ContextMenu } from "./ContextMenu";
import { FormatBadge } from "./FormatBadge";
import { RenderModeBadge } from "./RenderModeBadge";
import { StatusBadge } from "./StatusBadge";
import { ThumbImage } from "./ThumbImage";
import { ViewerKindBadge } from "./ViewerKindBadge";
import { ViewerKindControl } from "./ViewerKindControl";
import { assetDpiDetail, assetFormatLabel, assetShowsChannelBadge, assetViewerKind } from "./assetMeta";

interface AssetCardProps {
  asset: AssetListItem;
  onRename?: (asset: AssetListItem) => void;
  onDelete?: (asset: AssetListItem) => void;
  onRender?: (asset: AssetListItem, mode: "channels" | "composite", options?: { layers?: boolean }) => void;
  onCompare?: (asset: AssetListItem) => void;
  /** Só aparece em documento: comparar versões é do comparador semântico. */
  onCompareVersion?: (asset: AssetListItem) => void;
  onHistory?: (asset: AssetListItem) => void;
  onRenderLogs?: (asset: AssetListItem) => void;
  onDownload?: (asset: AssetListItem) => void;
  rendering?: boolean;
}

export const AssetCard = memo(function AssetCard({
  asset,
  onRename,
  onDelete,
  onRender,
  onCompare,
  onCompareVersion,
  onHistory,
  onRenderLogs,
  onDownload,
  rendering = false,
}: AssetCardProps) {
  const selected = useFileManagerStore((state) => state.selectedAssetId === asset.id);
  const setSelectedAssetId = useFileManagerStore((state) => state.setSelectedAssetId);
  // DPI é detalhe de produção: interessa a quem opera o render, não a quem
  // só envia e visualiza arquivo (F1.21).
  const isAdmin = useAuthStore((state) => state.user?.is_admin === true);
  const formatLabel = assetFormatLabel(asset);
  // Cada card é um arquivo, inclusive no conjunto de separações: o produto
  // decidiu que os sete ficam visíveis (`F-T.0-plano.md`, §15), e um card
  // chamado pela pasta faria sete linhas com o mesmo nome. O selo abaixo é que
  // diz "esta é uma de N"; convergir link, estado e nome da peça é a F-T.9.
  const separationCount = asset.separation_count ?? null;
  const cardTitle = asset.filename;
  const dpiDetail = isAdmin ? assetDpiDetail(asset) : null;
  const showChannelBadge = assetShowsChannelBadge(asset);
  const renderDisabled =
    rendering ||
    !asset.current_file_id ||
    asset.status === "queued" ||
    asset.status === "rendering" ||
    asset.status === "preparing";
  const [tipoDeViewerAberto, setTipoDeViewerAberto] = useState(false);

  const menuItems = useMemo(
    () =>
      onRename || onDelete || onRender || onCompare || onCompareVersion || onHistory || onDownload
        ? [
            ...(onRender
              ? [
                  {
                    label: rendering ? "Renderizando..." : "Renderizar",
                    onSelect: () => {
                      if (renderDisabled) return;
                      onRender(asset, "channels");
                    },
                  },
                ]
              : []),
            // Camadas OCG (rflow 2.4.2) não são o padrão: custam de 2,5 a 4× de
            // CPU nas páginas com camadas. É um pedido, ao lado do «Renderizar».
            ...(onRender && podeRenderizarComCamadas(asset)
              ? [
                  {
                    label: "Renderizar com camadas",
                    onSelect: () => {
                      if (renderDisabled) return;
                      onRender(asset, "channels", { layers: true });
                    },
                  },
                ]
              : []),
            // Trocar o viewer é pedir preparo e render de novo: aparece para
            // quem vê o «Renderizar», e só em PDF (29/09/2026).
            ...(onRender && podeTrocarViewer(asset)
              ? [{ label: "Tipo de viewer…", onSelect: () => setTipoDeViewerAberto(true) }]
              : []),
            ...(onCompare
              ? [
                  {
                    label: "Comparar",
                    onSelect: () => onCompare(asset),
                  },
                ]
              : []),
            ...(onCompareVersion
              ? [
                  {
                    label: "Comparar com a versão anterior",
                    onSelect: () => onCompareVersion(asset),
                  },
                ]
              : []),
            ...(onDownload
              ? [{ label: "Baixar original" as const, onSelect: () => onDownload(asset) }]
              : []),
            ...(onHistory ? [{ label: "Histórico" as const, onSelect: () => onHistory(asset) }] : []),
            ...(onRenderLogs ? [{ label: "Logs do render" as const, onSelect: () => onRenderLogs(asset) }] : []),
            ...(onRename ? [{ label: "Renomear" as const, onSelect: () => onRename(asset) }] : []),
            ...(onDelete
              ? [
                  {
                    label: "Excluir" as const,
                    destructive: true as const,
                    onSelect: () => onDelete(asset),
                  },
                ]
              : []),
          ]
        : [],
    [
      asset,
      onDelete,
      onDownload,
      onHistory,
      onRenderLogs,
      onRename,
      onRender,
      onCompare,
      onCompareVersion,
      renderDisabled,
      rendering,
    ],
  );

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => setSelectedAssetId(asset.id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setSelectedAssetId(asset.id);
        }
      }}
      className={`relative flex aspect-square cursor-pointer flex-col overflow-hidden rounded-lg border bg-ink-800/40 transition hover:bg-ink-800 ${
        selected
          ? "border-emerald-500 ring-1 ring-emerald-500"
          : "border-ink-700 hover:border-ink-500"
      }`}
    >
      {menuItems.length > 0 ? (
        <div className="absolute right-1 top-1 z-10">
          <ContextMenu items={menuItems} ariaLabel={`Ações de ${asset.filename}`} />
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-ink-900">
        {asset.thumb_url ? (
          <ThumbImage
            src={asset.thumb_url}
            alt={asset.filename}
            label={formatLabel}
            className="h-full w-full object-contain"
            width={320}
            height={320}
          />
        ) : (
          <div className="px-2 text-center text-[10px] text-ink-600">
            {asset.status === "expired"
              ? "Expirado"
              : asset.status === "rendering"
                ? "Gerando preview…"
                : asset.status === "preparing"
                  ? "Preparando…"
                  : asset.status === "queued"
                    ? "Em fila…"
                    : asset.status === "error"
                      ? "Erro no render"
                      : "Sem preview"}
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-col gap-1 p-2.5">
        <div className="truncate text-sm font-medium" title={asset.path}>
          {cardTitle}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <StatusBadge status={asset.status} />
          <FormatBadge label={formatLabel} />
          <ViewerKindBadge kind={assetViewerKind(asset)} manual={Boolean(asset.viewer_kind_override)} />
          <RenderModeBadge mode={asset.render_mode} hasChannels={showChannelBadge} />
          {separationCount ? (
            <span
              className="rounded bg-ink-700 px-1.5 py-0.5 text-[10px] text-ink-200"
              title="Separações do Esko nesta pasta — abrir qualquer uma leva ao conjunto inteiro"
            >
              {separationCount} {separationCount === 1 ? "separação" : "separações"}
            </span>
          ) : null}
          {asset.notes_count > 0 && (
            <span className="rounded bg-ink-700 px-1.5 py-0.5 text-[10px] text-ink-200">
              {asset.notes_count} notas
            </span>
          )}
        </div>

        <div className="text-[11px] text-ink-400">
          Tam. {formatBytes(asset.size_bytes)}
          {dpiDetail ? <span> / {dpiDetail}</span> : null}
        </div>

        <div className="mt-0.5 flex items-center gap-1.5">
          {asset.viewer_url && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                window.open(asset.viewer_url!, "_blank", "noopener,noreferrer");
              }}
              className="rounded bg-emerald-700 px-2 py-1 text-xs hover:bg-emerald-600"
            >
              Abrir
            </button>
          )}
          {rendering && (
            <span className="text-[11px] text-ink-400 italic">Renderizando...</span>
          )}
        </div>
      </div>
      <ViewerKindControl
        asset={asset}
        open={tipoDeViewerAberto}
        onClose={() => setTipoDeViewerAberto(false)}
      />
    </article>
  );
});
